import { APIRequestError } from '@/services/api';

/**
 * Central mapping of backend error codes to friendly German messages.
 *
 * Only include codes whose meaning is UNAMBIGUOUS across the whole app.
 * Context-dependent codes (UNAUTHORIZED, TOKEN_INVALID, FORBIDDEN, ...) must be
 * handled by the caller via the `fallback` or `overrides` arguments of
 * {@link getUserFacingError}, because the same code means different things in
 * different flows (e.g. UNAUTHORIZED = "wrong password" at login but "session
 * expired" everywhere else).
 */
export const ERROR_MESSAGES: Record<string, string> = {
  MAIL_ALREADY_EXISTS: 'Diese E-Mail-Adresse ist bereits registriert.',
  SUBSCRIPTION_ALREADY_EXISTS: 'Du hast dieses Paket bereits aktiv.',
  SUBSCRIPTION_CANCELED: 'Dieses Abonnement wurde bereits gekündigt.',
  REGISTER_TOKEN_RATE_LIMIT: 'Ein Bestätigungscode wurde bereits angefordert. Bitte warte einen Moment und versuche es dann erneut.',
  USER_QUESTION_RATE_LIMIT: 'Du hast gerade erst eine Frage gesendet. Bitte warte eine Minute.',
  FEATURE_DISABLED: 'Diese Funktion ist in deinem aktuellen Paket nicht enthalten.',
  CHAT_CLOSED: 'Dieser Chat wurde bereits geschlossen. Bitte starte einen neuen Chat.',
  CHAT_ACTIVE: 'Bitte warte, bis die aktuelle Antwort abgeschlossen ist.',
};

/**
 * Translates an error into a user-facing German message, never leaking the raw
 * (usually English) backend message to the end user.
 *
 * Resolution order:
 *   1. `overrides` for this error code (caller-provided, context-specific)
 *   2. the global {@link ERROR_MESSAGES} map (unambiguous codes)
 *   3. the caller-provided German `fallback`
 *
 * @param err       the caught error (any type)
 * @param fallback  context-specific German message shown when the code is unknown
 * @param overrides optional per-code German overrides for this call site
 */
export function getUserFacingError(
  err: unknown,
  fallback: string,
  overrides?: Record<string, string>,
): string {
  if (err instanceof APIRequestError && err.errorcode !== undefined) {
    const override = overrides?.[err.errorcode];
    if (override !== undefined) {
      return override;
    }
    const mapped = ERROR_MESSAGES[err.errorcode];
    if (mapped !== undefined) {
      return mapped;
    }
  }
  return fallback;
}
